import React, {useState} from 'react';
import {Box, ThemeProvider} from '@mui/material';
import NavBar from '../../pieces/navbar/navbar';
import myTheme from '../../styles/myStyles';
import DisplayListsPageWrapper from '../wrappers/displayListsPageWrapper';
import ListPageHeader from '../../pieces/listPageHeader';
import {ListViewHeader} from '../jobs/components/listViewHeader';
import usePagination from '../../hooks/usePagination';
import {useGridViewState} from '../../hooks/useGridViewState';
import {globalUseClasses} from '../../styles/globalClasses';
import PartnersGrid from './partnersGrid';
import CreatePartnerDialog from './createPartnerDialog';
import PartnerDetailDialog from './partnerDetailDialog';
import SecretRevealDialog from './secretRevealDialog';

/**
 * Partner API credentials.
 *
 * iPermit staff only. There is no partner-facing page and no partner login: a
 * partner's identity is its client id and secret, delivered out of band.
 */
const PartnersView = () => {
  const viewController = useGridViewState();
  const pagination = usePagination();
  const classes = globalUseClasses();

  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(undefined);
  const [secret, setSecret] = useState(undefined);

  const openPartner = (row) => {
    if (row?.id) setDetailId(row.id);
  };

  /**
   * Refreshes the grid on the next tick rather than during the current render.
   *
   * `refreshGrid` bumps `refreshTimestamp`, which re-runs the AG Grid
   * datasource. Called while a dialog is opening or closing, that lands in the
   * middle of the grid's own render pass and AG Grid throws:
   *
   *   "cannot get grid to draw rows when it is in the middle of drawing rows"
   *
   * There is no error boundary above this page, so that exception unmounts the
   * whole React tree and the admin sees a white screen. Deferring is the remedy
   * AG Grid's own message prescribes. It is timing-dependent, which is why this
   * reproduced on production but never locally.
   */
  const refreshGridDeferred = () => {
    setTimeout(() => viewController.refreshGrid(), 0);
  };

  const handleCreated = (partner) => {
    setCreateOpen(false);
    refreshGridDeferred();
    // Straight into the detail dialog: a partner that has just been created can
    // do nothing until it is approved, given a secret and assigned a company,
    // and returning the admin to the list hides all three of those steps.
    if (partner?.id) setDetailId(partner.id);
  };

  const getHeaderComponent = () => {
    let title = 'Partner API';
    if (pagination.totalItemsCount) {
      title = `${title} (${pagination.totalItemsCount})`;
    }
    return (
      <ListViewHeader
        title={title}
        pagination={pagination}
        viewController={viewController}
      />
    );
  };

  const headerComponent = (
    <ListPageHeader
      viewController={viewController}
      headComponent={getHeaderComponent()}
      addHandler={() => setCreateOpen(true)}
      showAddButton
      totalItems={pagination.totalItemsCount}
    >
      {pagination.ItemsController}
    </ListPageHeader>
  );

  return (
    <>
      <NavBar/>
      <ThemeProvider theme={myTheme}>
        <Box className={classes.page}>
          <DisplayListsPageWrapper headerComponent={headerComponent}>
            <PartnersGrid
              pagination={pagination}
              viewController={viewController}
              onOpen={openPartner}
            />
          </DisplayListsPageWrapper>

          <CreatePartnerDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={handleCreated}
          />

          <PartnerDetailDialog
            open={!!detailId}
            partnerId={detailId}
            onClose={() => setDetailId(undefined)}
            onChanged={refreshGridDeferred}
            onSecretGenerated={setSecret}
          />

          <SecretRevealDialog
            open={!!secret}
            onClose={() => setSecret(undefined)}
            partnerName={secret?.partnerName}
            clientId={secret?.clientId}
            clientSecret={secret?.clientSecret}
            rotated={secret?.rotated}
            previousSecretValidUntil={secret?.previousSecretValidUntil}
          />
        </Box>
      </ThemeProvider>
    </>
  );
};

export default PartnersView;
